import './OverrideInterface.scss';
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMiwiStore } from '../../../store';
import ModalActionButtons from '../../modalActionButtons/ModalActionButtons';
import { type OverrideDevice } from '../../../types/override';
import DealershipList from '../../dealershipList/DealershipList';

type OverrideInterfaceProps = {
    onClose: () => void;
};

const OverrideInterface = ({ onClose }: OverrideInterfaceProps) => {
    const { t } = useTranslation();
    const { 
        overrideDealerId, overrideLoading, overrideSessionId, overrideDevices: devices, overrideDeviceId,
        connectDevice, miwiLogout,setOverrideDealerId, fetchNaapDevices
    } = useMiwiStore();
    const [overrideDevices, setOverrideDevices] = useState<OverrideDevice[]>([]);
    const [login, setLogin] = useState<boolean>(true);
    const [openConfirmConnect, setOpenConfirmConnect] = useState(false);
    const [openConfirmLogout, setOpenConfirmLogout] = useState(false);
    const [openConfirmDisconnect, setOpenConfirmDisconnect] = useState(false);
    const [sessionId, setSessionId] = useState<string>(overrideSessionId);

    useEffect(() => {
        setOverrideDevices(devices);
    }, [devices]);

    useEffect(() => {
        setSessionId(overrideSessionId);
    }, [overrideSessionId]);

    const getDevicesList = async (dealer) => {
        setOverrideDealerId(dealer.kvps);
        localStorage.setItem('overrideDealerId', dealer.kvps)
        await fetchNaapDevices()
        setLogin(!login);
    };

    const checkStatus = device => {
            return checkAvailability(device);
    };
    
    const checkAvailability = device => {
        if (device.online && device.sessionId && device.sessionId !== sessionId && device.thingName !== overrideDeviceId) {
            return 'in use';
        }
        if (device.online && device.sessionId && device.sessionId === sessionId || sessionId && device.online && device.thingName === overrideDeviceId
        ) {
            return 'connected';
        }
        if (device.online) {
            return 'online';
        } else return 'offline';
    };

    const renderDevices = () => {
        const list = overrideDevices.map(device => renderDevice(device));
        return list;
    };

    const renderDevice = device => {
        const itemClasses = classnames('devices-item', {
            unavailable: checkAvailability(device) === 'offline',
            connected: checkAvailability(device) === 'connected' || checkAvailability(device) === 'in use'
        });
         const pClasses = classnames('availability', {
            selected: checkAvailability(device) === 'connected',
            playing: checkAvailability(device) === 'in use'

        });
        const spanClasses = classnames('device', {
            selected: checkAvailability(device) === 'connected',
            playing: checkAvailability(device) === 'in use'

        });
        const item = (
            <div
                className={itemClasses}
                onClick={() => selectDevice(device)}
                key={device.thingName}
            >
                <span className={spanClasses}/>
                <p className="device-name">
                    {device.displayName}
                    <br />
                    {device.thingName}
                </p>
                <p className={pClasses}>{checkStatus(device)}</p>
            </div>
        );
        return item;
    };

    const logout = () => {
        miwiLogout();    
        onClose();
    };

    const openConfirmConnectHandle = () => {
        setOpenConfirmConnect(!openConfirmConnect);
    };

    const openConfirmDisconnectHandle = () => {
        setOpenConfirmDisconnect(!openConfirmDisconnect);
    };

    const openConfirmLogoutHandle = () => {
        setOpenConfirmLogout(!openConfirmLogout);
    };

    const onConfirmClick = async () => {
        await disconnectDevice();    
        setOpenConfirmConnect(!openConfirmConnect);
    };

    const selectDevice = async device => {
        await connectDevice(device.thingName);
    };

    const disconnectDevice = async () => {
        await disconnectDevice();    
        openConfirmDisconnectHandle();
    };

    const classes = classnames('b-override', {
        'is-loading': overrideLoading
    });
    if (
        !overrideDealerId &&
        !openConfirmConnect &&
        !openConfirmDisconnect &&
        !openConfirmLogout
    ) {
        return (
            <div className={classes}>
                <h3 className="header">{t('override_login')}</h3>
                <p className="header">{t('override_select_dealer')}:</p>
                <DealershipList
                    onClick={getDevicesList}
                />
            </div>
        );
    }
    if (
        overrideDealerId &&
        !openConfirmConnect &&
        !openConfirmDisconnect &&
        !openConfirmLogout
    ) {
        return (
            <div className={classes}>
                <h3 className="header">{t('connect_with_device')}</h3>
                <p>{t('logged_in_as')}: {overrideDealerId}</p>
                <div className="devices-list">
                    {overrideDevices.length > 0 ? (
                        renderDevices()
                    ) : (
                        <p className="no-devices">{t('override_no_devices')}</p>
                    )}
                </div>
                <ModalActionButtons
                    localeCancel={`Override ${t('logout_action')}`}
                    localeAction={t('device_disconnect')}
                    onCancel={openConfirmLogoutHandle}
                    onAction={openConfirmDisconnectHandle}
                    disabled={!sessionId}
                    isLoading={overrideLoading}
                />
            </div>
        );
    }
    if (openConfirmConnect) {
        return (
            <div className={classes}>
                <h3 className="header">{t('connect_with_device')}</h3>
                <p className="header">{t('logged_in_as')}: {overrideDealerId}</p>
                <div className="message">
                    <p>{t('override_confirm_connect')}</p>
                </div>
                <ModalActionButtons
                    localeAction={t('override_connect')}
                    localeCancel={t('cancel')}
                    onAction={onConfirmClick}
                    onCancel={openConfirmConnectHandle}
                    isLoading={overrideLoading}
                />
            </div>
        );
    }
    if (openConfirmDisconnect) {
        return (
            <div className={classes}>
                <h3 className="header">{t('device_disconnect')}</h3>
                <div className="message">
                    <p>{t('override_confirm_disconnect')}</p>
                </div>
                <ModalActionButtons
                    localeAction={t('override_disconnect')}
                    localeCancel={t('cancel')}
                    onAction={disconnectDevice}
                    onCancel={openConfirmDisconnectHandle}
                    isLoading={overrideLoading}
                />
            </div>
        );
    }
    if (openConfirmLogout) {
        return (
            <div className={classes}>
                <h3 className="header">{t('override_logout')}</h3>
                <div className="message">
                    <p>{t('override_confirm_logout')}</p>
                </div>
                <ModalActionButtons
                    localeAction={t('logout')}
                    localeCancel={t('cancel')}
                    onAction={logout}
                    onCancel={openConfirmLogoutHandle}
                    isLoading={overrideLoading}
                />
            </div>
        );
    }
    return '';
};

export default OverrideInterface;
